import React, { createContext, useContext, ReactNode, useCallback, useMemo } from "react";
import { useHistory, generatePath } from "react-router-dom";
import { useGetAllStorefrontsQuery } from "../../datastore/TundraStorefrontDataStore";
import { useCreateStoreMutation } from "../../datastore/StoreDataStore";
import { NavigationHelper } from "../../navigation/PrevLocationState";
import { TundraStorefrontSummary } from "../../../../common/models";
import { StoreCreateFormData, useStoreCreateForm } from "./hooks/useStoreCreateForm";
import { GenericFormReturn } from "../../common/hooks/useGenericForm";
import { parseThemeNumbers } from "./utils/themeUtils";
import { toaster } from "evergreen-ui";

export interface StoreCreateContextType extends GenericFormReturn<StoreCreateFormData> {
    storefrontsData: TundraStorefrontSummary[] | null;
    storefrontsLoading: boolean;
    storefrontsError: Error | undefined;
    submitForm: () => void;
    submitFormLoading: boolean;
    submitFormError: any;
    submitFormCalled: boolean;
}

const StoreCreateContext = createContext<StoreCreateContextType | undefined>(undefined);

export const useStoreCreateContext = (): StoreCreateContextType => {
    const context = useContext(StoreCreateContext);
    if (!context) {
        throw new Error('useStoreCreateContext must be used within a StoreCreateProvider');
    }
    return context;
};

interface StoreCreateProviderProps {
    children: ReactNode;
}

export const StoreCreateProvider: React.FC<StoreCreateProviderProps> = ({ children }) => {
    const history = useHistory();

    const {
        data: tundraStorefrontList,
        loading: storefrontsLoading,
        error: storefrontsError
    } = useGetAllStorefrontsQuery();

    const [
        createStore,
        {
            loading: submitFormLoading,
            error: submitFormError,
            called: submitFormCalled,
            data: createdStore,
        }
    ] = useCreateStoreMutation();

    const submitOperationConfig = useMemo(() => ({
        loading: submitFormLoading,
        called: submitFormCalled,
        error: submitFormError,
        successMessage: "Store was created successfully.",
        failureMessage: "Error creating store. Check the console logs.",
        onSuccess: () => {
            if (createdStore) {
                history.push(
                    generatePath(NavigationHelper.createStorePath(createdStore.id))
                );
            }
        },
    }), [submitFormLoading, submitFormCalled, submitFormError, createdStore, history]);

    const storeCreateForm = useStoreCreateForm(tundraStorefrontList, submitOperationConfig);

    // Filter out empty strings and non-numeric values from theme numbers input field
    const themeNumbers = parseThemeNumbers(storeCreateForm.formValues.themes);

    const submitForm = useCallback(() => {
        createStore({
            storeName: storeCreateForm.formValues.storeName,
            tundraSubdomainPrefix: storeCreateForm.formValues.tundraSubdomainPrefix,
            existingTundraStorefrontId: storeCreateForm.formValues.existingStorefrontId,
            existingTundraStorefrontThemeNumbers: themeNumbers,
        });
    }, [createStore, storeCreateForm.formValues, themeNumbers]);

    const value: StoreCreateContextType = {
        ...storeCreateForm,
        storefrontsData: tundraStorefrontList,
        storefrontsLoading,
        storefrontsError,
        submitForm,
        submitFormLoading,
        submitFormError,
        submitFormCalled,
    };

    return (
        <StoreCreateContext.Provider value={value}>
            {children}
        </StoreCreateContext.Provider>
    );
};
